import { View, Text, styled } from 'tamagui'
import { MessageCircle, Check, X, UserMinus } from '@tamagui/lucide-icons'
import { Avatar, StatusDot } from '../../common'
import type { User, Friendship } from '@/types'
import { useAppStore } from '@/store'
import { dmChannels } from '@/mocks/dms'

const Row = styled(View, {
  flexDirection: 'row',
  alignItems: 'center',
  height: 56,
  paddingHorizontal: 12,
  borderRadius: '$borderRadiusLg',
  gap: 12,
  hoverStyle: {
    backgroundColor: '$sidebarHover',
  },
  // @ts-expect-error -- web transition
  style: { transition: 'background-color 150ms ease' },
})

const Info = styled(View, { flex: 1, gap: 2 })

const Name = styled(Text, {
  fontFamily: '$fontBody',
  fontSize: 14,
  fontWeight: '500',
  color: '$surfaceForeground',
})

const StatusText = styled(Text, {
  fontFamily: '$fontBody',
  fontSize: 12,
  color: '$sidebarMuted',
})

const ActionButton = styled(View, {
  width: 32,
  height: 32,
  borderRadius: '$borderRadius2xl',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: '$surfaceRaised',
  cursor: 'pointer',
  hoverStyle: {
    backgroundColor: '$sidebarHover',
  },
})

const PendingLabel = styled(View, {
  paddingHorizontal: 8,
  paddingVertical: 3,
  borderRadius: '$borderRadiusSm',
  backgroundColor: '$sidebarActive',
})

interface FriendRowProps {
  friendship: Friendship
  user: User
}

export function FriendRow({ friendship, user }: FriendRowProps) {
  const { setActiveDmChannel, navigateToDms } = useAppStore()

  const handleMessage = () => {
    const dm = dmChannels.find(d => d.participantIds.includes(user.id))
    if (dm) {
      navigateToDms()
      setActiveDmChannel(dm.id)
    }
  }

  const statusLabels: Record<string, string> = {
    online: 'Online',
    idle: 'Idle',
    dnd: 'Do Not Disturb',
    offline: 'Offline',
  }

  return (
    <Row>
      <Avatar name={user.displayName} color={user.roleColor || '#8B95A8'} status={user.status} size="md" />
      <Info>
        <View flexDirection="row" alignItems="center" gap={6}>
          <Name>{user.displayName}</Name>
        </View>
        <View flexDirection="row" alignItems="center" gap={4}>
          <StatusDot status={user.status} size={8} />
          <StatusText>{statusLabels[user.status] || user.status}</StatusText>
        </View>
      </Info>

      {friendship.status === 'accepted' && (
        <ActionButton onPress={handleMessage}>
          <MessageCircle size={16} color="$sidebarMuted" />
        </ActionButton>
      )}

      {friendship.status === 'pending_incoming' && (
        <View flexDirection="row" gap={6}>
          <PendingLabel>
            <Text fontFamily="$fontBody" fontSize={11} color="$sidebarMuted">Incoming</Text>
          </PendingLabel>
          <ActionButton>
            <Check size={16} color="$success" />
          </ActionButton>
          <ActionButton>
            <X size={16} color="$error" />
          </ActionButton>
        </View>
      )}

      {friendship.status === 'pending_outgoing' && (
        <PendingLabel>
          <Text fontFamily="$fontBody" fontSize={11} color="$sidebarMuted">Pending</Text>
        </PendingLabel>
      )}

      {friendship.status === 'blocked' && (
        <ActionButton>
          <UserMinus size={16} color="$error" />
        </ActionButton>
      )}
    </Row>
  )
}
